import classNames from 'classnames';
import React, { useMemo } from 'react';

import { ObjectSensorType } from 'entities/common';
import { SensorMeasurement } from 'utils/constants';
import { formatLocalDateTime, parseServerDate } from 'utils/helpers';
import { Battery } from '../Battery';

const METRIC_COLUMN_ORDER = [
  '4097', // Air Temperature
  '4098', // Air Humidity
  '4100', // CO2
  '4101', // Barometric Pressure
  '4105', // Wind Speed
  '4104', // Wind Direction
  '4146', // PM2.5
  '4147', // PM10
  '4148', // Noise
  '4099', // Light Intensity
  '4102', // Soil Temperature
  '4103', // Soil Moisture
  '4108', // Electrical Conductivity
];

/** Short column labels */
const METRIC_SHORT_LABELS: Record<string, string> = {
  '4097': 'Air temp',
  '4098': 'Air humid',
  '4100': 'CO2',
  '4101': 'Pressure',
  '4105': 'Wind speed',
  '4104': 'Wind dir',
  '4146': 'PM2.5',
  '4147': 'PM10',
  '4148': 'Noise',
  '4099': 'Light',
  '4102': 'Soil temp',
  '4103': 'Soil moist',
  '4108': 'EC',
};

/** Sort priority: S800 → S2102 → S2105, others at the end */
const SENSOR_TYPE_ORDER: Record<string, number> = {
  sensecap_s800: 0,
  sensecap_s2102: 1,
  sensecap_s2105: 2,
};

/** Human-readable sensor type label */
function sensorTypeLabel(sensorType: string): string {
  const match = sensorType.match(/s(\d+)/i);
  return match ? `S${match[1]}` : sensorType;
}

/** Format date as DD.MM.YYYY HH:MM in the user's local zone */
function formatDate(dateStr: string): string {
  if (Number.isNaN(parseServerDate(dateStr).valueOf())) return '-';
  return formatLocalDateTime(dateStr);
}

/** Get the latest metric entry for a sensor */
function getLatestMetric(sensor: ObjectSensorType) {
  return sensor.latest_metrics?.[0] ?? null;
}

/** Get status text and whether it's offline */
function getSensorStatus(sensor: ObjectSensorType): { text: string; isOffline: boolean } {
  if (sensor.is_deleted) return { text: '-', isOffline: false };
  if (!sensor.active) return { text: 'Turned off', isOffline: true };
  const metric = getLatestMetric(sensor);
  if (!metric) return { text: '-', isOffline: false };
  if (metric.online_status == null) return { text: '-', isOffline: false };
  const online = !!metric.online_status;
  return { text: online ? 'Online' : 'Offline', isOffline: !online };
}

/** Get battery text and whether it's low */
function getSensorBattery(sensor: ObjectSensorType): { text: string; isLow: boolean } {
  if (sensor.is_deleted || !sensor.active) return { text: '-', isLow: false };
  const metric = getLatestMetric(sensor);
  if (!metric) return { text: '-', isLow: false };
  if (metric.battery_status == null) return { text: '-', isLow: false };
  const high = !!metric.battery_status;
  return { text: high ? 'High' : 'Low', isLow: !high };
}

/** Get last update text */
function getLastUpdate(sensor: ObjectSensorType): string {
  if (sensor.is_deleted) return 'Deleted';
  if (!sensor.active) return 'Turned off';
  const metric = getLatestMetric(sensor);
  if (!metric) return '-';
  return formatDate(metric.time);
}

/** Get metric value with unit for a sensor and measurement ID */
function getMetricValue(sensor: ObjectSensorType, measurementId: string): string {
  const metric = getLatestMetric(sensor);
  if (!metric || typeof metric.points === 'string') return '-';
  const point = (metric.points || [])?.find((p) => String(p.measurement_id) === measurementId);
  if (!point) return '-';
  const def = SensorMeasurement[measurementId];
  const unit = def?.unit || '';
  return `${point.measurement_value}${unit ? ' ' + unit : ''}`;
}

type Props = {
  sensors: ObjectSensorType[];
  className?: string;
};

export const ObjectSensorsTable: React.FC<Props> = ({ sensors, className }) => {
  /** Sort sensors by type priority */
  const sortedSensors = useMemo(() => {
    return [...sensors]?.sort((a, b) => {
      const orderA = SENSOR_TYPE_ORDER[a.sensor_type] ?? 999;
      const orderB = SENSOR_TYPE_ORDER[b.sensor_type] ?? 999;
      return orderA - orderB;
    });
  }, [sensors]);

  /** Dynamic metric columns — only show columns that have at least one value */
  const activeMetricColumns = useMemo(() => {
    return METRIC_COLUMN_ORDER.filter((measurementId) =>
      sensors?.some((sensor) => {
        const metric = getLatestMetric(sensor);
        if (!metric || typeof metric.points === 'string') return false;
        return metric?.points?.some((p) => String(p.measurement_id) === measurementId);
      }),
    );
  }, [sensors]);

  if (!sensors.length) return null;

  return (
    <section className={classNames('dashboard-object-sensors', className)}>
      <div className={classNames('dashboard-object-sensors__header')}>
        <h2 className={classNames('dashboard-object-sensors__title')}>Object sensors</h2>
        <p className={classNames('dashboard-object-sensors__subtitle')}>
          sensors assigned to the object level and shared across all zones
        </p>
      </div>

      <div className={classNames('dashboard-object-sensors__table-wrapper')}>
        <table className={classNames('dashboard-object-sensors__table')}>
          <thead>
            <tr>
              <th>Type</th>
              <th>Status</th>
              <th>Battery</th>
              {(activeMetricColumns || [])?.map((id) => (
                <th key={id}>{METRIC_SHORT_LABELS[id] || SensorMeasurement[id]?.label || id}</th>
              ))}
              <th>Last update</th>
            </tr>
          </thead>
          <tbody>
            {(sortedSensors || [])?.map((sensor) => {
              const status = sensor.active
                ? getSensorStatus(sensor)
                : { text: '-', isOffline: false };
              const battery = getSensorBattery(sensor);
              const lastUpdate = getLastUpdate(sensor);
              const isAlert = status.isOffline || battery.isLow;

              return (
                <tr
                  key={sensor.id}
                  className={classNames({
                    'row-alert': isAlert,
                    'row-online': !isAlert && status.text === 'Online',
                  })}
                >
                  <td data-label="Type">{sensorTypeLabel(sensor.sensor_type)}</td>
                  <td data-label="Status">
                    <span
                      className={classNames({
                        'status-online': status.text === 'Online',
                        'status-offline': status.text === 'Offline' || status.text === 'Turned off',
                      })}
                    >
                      {status.text}
                    </span>
                  </td>
                  <td data-label="Battery">
                    <Battery sensor={{ battery: battery.text }} />
                  </td>
                  {(activeMetricColumns || [])?.map((measurementId) => (
                    <td
                      key={measurementId}
                      data-label={METRIC_SHORT_LABELS[measurementId] || measurementId}
                    >
                      {sensor?.active ? getMetricValue(sensor, measurementId) : '-'}
                    </td>
                  ))}
                  <td
                    data-label="Last update"
                    className={classNames('dashboard-object-sensors__last-update')}
                  >
                    {lastUpdate}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
};
